import { useEffect, useState } from "react";
import { Switch, InputNumber, Input, Button, Spin, Tag, Table, Modal, Tabs, Space, Upload, Radio, message } from "antd";
import { EditOutlined, UploadOutlined, DownloadOutlined } from "@ant-design/icons";
import { t } from "i18next";
import ApiService from "../../core/service/ApiService";
import NotificationTemplateEditor from "./NotificationTemplateEditor";
import { DataGridProvider } from "../../core/components/data-grid/DataGridContext";
import NotificationOutboxGrid from "./NotificationOutboxGrid";
import NotificationOutboxFilter from "./NotificationOutboxFilter";

const CHANNELS = [
    { value: 1, label: 'SMS' },
    { value: 2, label: t('Email') },
    { value: 4, label: t('WhatsApp') },
];

const EVENT_LABELS = {
    APPOINTMENT_CREATED: 'Appointment Created',
    APPOINTMENT_REMINDER: 'Appointment Reminder',
    APPOINTMENT_RESCHEDULED: 'Appointment Rescheduled',
    APPOINTMENT_COMPLETED: 'Appointment Completed',
    APPOINTMENT_CANCELLED: 'Appointment Cancelled',
    KVKK_NOTICE: 'Clarification Message (First Contact)',
    KVKK_CODE: 'Commercial Message Consent Code',
    LOGIN_CODE: 'Login Verification Code',
};

const DOC_TYPE_LABELS = {
    AYDINLATMA: 'Clarification Text',
    TICARI: 'Commercial Electronic Message',
};

const isAppointmentEvent = (eventType) => (eventType || '').startsWith('APPOINTMENT_');

const channelLabel = (value) => CHANNELS.find(c => c.value === value)?.label || value;

export default function NotificationSettingsForm() {
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [placeholders, setPlaceholders] = useState([]);
    const [editingEvent, setEditingEvent] = useState(null);
    const [balanceLoading, setBalanceLoading] = useState(false);
    const [balanceResult, setBalanceResult] = useState(null);
    const [activeTab, setActiveTab] = useState('general');
    const [kvkkDocs, setKvkkDocs] = useState([]);
    const [kvkkUploading, setKvkkUploading] = useState(null);
    const [global, setGlobal] = useState({
        notificationsEnabled: false,
        kvkkEnabled: false,
        testCustomId: '',
        ticariConsentEnabled: false,
        aydinlatmaOnRegister: false,
        smsBalanceThreshold: null,
        smsBalanceAlertRecipients: '',
    });
    const [rules, setRules] = useState([]);

    const load = async () => {
        setLoading(true);
        const [settingsRes, placeholdersRes] = await Promise.all([
            ApiService.NotificationSettings.GetSettings(),
            ApiService.NotificationSettings.GetPlaceholders(),
        ]);

        if (placeholdersRes.success) setPlaceholders(placeholdersRes.data || []);

        if (settingsRes.success) {
            const d = settingsRes.data;
            setGlobal({
                notificationsEnabled: d.notificationsEnabled,
                kvkkEnabled: d.kvkkEnabled,
                testCustomId: d.testCustomId || '',
                ticariConsentEnabled: d.ticariConsentEnabled,
                aydinlatmaOnRegister: d.aydinlatmaOnRegister,
                smsBalanceThreshold: d.smsBalanceThreshold ?? null,
                smsBalanceAlertRecipients: d.smsBalanceAlertRecipients || '',
            });
            setRules((d.rules || []).map(r => ({
                eventType: r.eventType,
                requiresKvkk: r.requiresKvkk,
                enabled: r.enabled,
                channels: r.channels || [],
                leadMinutes: r.leadMinutes,
                delayMinutes: r.delayMinutes,
                thresholdMinutes: r.thresholdMinutes,
                morningHour: r.morningHour,
                eveningHour: r.eveningHour,
                earlyThresholdHour: r.earlyThresholdHour,
                maskName: r.maskName,
                // templates array -> map keyed by channel for easy editing
                templates: Object.fromEntries((r.templates || []).map(tpl => [tpl.channel, { title: tpl.title, body: tpl.body }])),
            })));
        }
        setLoading(false);
    };

    const loadKvkkDocs = async () => {
        const res = await ApiService.NotificationSettings.GetKvkkDocuments();
        if (res.success) setKvkkDocs(res.data || []);
    };

    useEffect(() => { load(); loadKvkkDocs(); }, []);

    const uploadKvkkDoc = (documentType) => async (file) => {
        if (file.type !== 'application/pdf') {
            message.error(t('Only PDF files are allowed.'));
            return Upload.LIST_IGNORE;
        }
        setKvkkUploading(documentType);
        try {
            const res = await ApiService.NotificationSettings.UploadKvkkDocument(documentType, file);
            if (res.success) {
                message.success(t('Uploaded'));
                await loadKvkkDocs();
            }
        } finally {
            setKvkkUploading(null);
        }
        return false; // antd Upload otomatik göndermesin
    };

    const activateKvkkDoc = async (id) => {
        const res = await ApiService.NotificationSettings.SetActiveKvkkDocument(id);
        if (res.success) await loadKvkkDocs();
    };

    const saveKvkkDocUrl = async (id, url, current) => {
        if ((url || '') === (current || '')) return; // değişmediyse kaydetme
        await ApiService.NotificationSettings.SetKvkkDocumentUrl(id, url || '');
        await loadKvkkDocs();
    };

    const updateRule = (eventType, patch) => {
        setRules(prev => prev.map(r => r.eventType === eventType ? { ...r, ...patch } : r));
    };

    const toggleChannel = (eventType, channel, enabled) => {
        setRules(prev => prev.map(r => {
            if (r.eventType !== eventType) return r;
            const channels = enabled
                ? Array.from(new Set([...(r.channels || []), channel]))
                : (r.channels || []).filter(c => c !== channel);
            return { ...r, channels };
        }));
    };

    const updateTemplate = (eventType, channel, patch) => {
        setRules(prev => prev.map(r => {
            if (r.eventType !== eventType) return r;
            const templates = { ...r.templates, [channel]: { ...(r.templates[channel] || {}), ...patch } };
            return { ...r, templates };
        }));
    };

    const save = async () => {
        setSaving(true);
        try {
            const payload = {
                ...global,
                rules: rules.map(r => ({
                    eventType: r.eventType,
                    enabled: r.enabled,
                    requiresKvkk: r.requiresKvkk,
                    channels: r.channels,
                    leadMinutes: r.leadMinutes,
                    delayMinutes: r.delayMinutes,
                    thresholdMinutes: r.thresholdMinutes,
                    morningHour: r.morningHour,
                    eveningHour: r.eveningHour,
                    earlyThresholdHour: r.earlyThresholdHour,
                    maskName: r.maskName,
                    templates: (r.channels || []).map(ch => ({
                        channel: ch,
                        title: r.templates[ch]?.title || null,
                        body: r.templates[ch]?.body || null,
                    })),
                })),
            };
            await ApiService.NotificationSettings.SaveSettings(payload);
        } finally {
            setSaving(false);
        }
    };

    const queryBalance = async () => {
        setBalanceLoading(true);
        setBalanceResult(null);
        try {
            const res = await ApiService.NotificationSettings.GetSmsBalance();
            const data = res.data || {};
            setBalanceResult({
                otpSms: data.otpSmsBalance ?? null,
                sms: data.smsBalance ?? null,
                message: data.message || res.message || null,
                raw: data.raw ?? null,
            });
        } finally {
            setBalanceLoading(false);
        }
    };

    if (loading) return <div style={{ textAlign: 'center', padding: 40 }}><Spin /></div>;

    const editingRule = rules.find(r => r.eventType === editingEvent);

    const columns = [
        {
            title: t('Notification Type'),
            dataIndex: 'eventType',
            render: (eventType) => t(EVENT_LABELS[eventType] || eventType),
        },
        {
            title: t('Status'),
            dataIndex: 'enabled',
            width: 90,
            render: (_, rule) => (
                <Switch
                    size="small"
                    checked={rule.enabled}
                    onChange={(v) => updateRule(rule.eventType, { enabled: v })}
                />
            ),
        },
        {
            title: t('Commercial consent'),
            dataIndex: 'requiresKvkk',
            width: 130,
            align: 'center',
            render: (_, rule) => (
                isAppointmentEvent(rule.eventType)
                    ? <Switch
                        size="small"
                        checked={rule.requiresKvkk}
                        onChange={(v) => updateRule(rule.eventType, { requiresKvkk: v })}
                    />
                    : <span style={{ color: '#bfbfbf' }}>—</span>
            ),
        },
        {
            title: t('Channels'),
            dataIndex: 'channels',
            render: (_, rule) => (
                (rule.channels || []).length
                    ? rule.channels.map(ch => <Tag key={ch} style={{ marginInlineEnd: 4 }}>{channelLabel(ch)}</Tag>)
                    : <span style={{ color: '#bfbfbf' }}>—</span>
            ),
        },
        {
            title: t('Actions'),
            key: 'actions',
            width: 110,
            align: 'right',
            render: (_, rule) => (
                <Button size="small" icon={<EditOutlined />} onClick={() => setEditingEvent(rule.eventType)}>
                    {t('Edit')}
                </Button>
            ),
        },
    ];

    return (
        <div>
            <Tabs
                activeKey={activeTab}
                onChange={setActiveTab}
                items={[
                    {
                        key: 'general',
                        label: t('General Settings'),
                        children: (
            <div style={{ maxWidth: 760 }}>
                <div style={{ display: 'flex', gap: 32, flexWrap: 'wrap' }}>
                    <div style={{ flex: 1, minWidth: 260 }}>
                        <Section title={t('Notifications')}>
                            <SwitchRow label={t('Enable Notifications')} checked={global.notificationsEnabled}
                                onChange={(v) => setGlobal(g => ({ ...g, notificationsEnabled: v }))} />
                            {/* Giriş doğrulaması (2FA) buradan çıktı: tek sistem anahtarı artık
                                Ayarlar → Yapılandırma altında (App.MfaEnabled), kullanıcı anahtarı ise
                                kullanıcı formunda. Kanal başına ayar yok. */}
                            <SwitchRow label={t('Collect commercial/marketing consent')} checked={global.ticariConsentEnabled}
                                onChange={(v) => setGlobal(g => ({ ...g, ticariConsentEnabled: v }))} />
                            <SwitchRow label={t('Send clarification SMS on patient registration')} checked={global.aydinlatmaOnRegister}
                                onChange={(v) => setGlobal(g => ({ ...g, aydinlatmaOnRegister: v }))} />
                        </Section>
                    </div>
                    <div style={{ flex: 1, minWidth: 260 }}>
                        <Section title={t('Test Mode')}>
                            <Field label={t('Test Mode Patient ID')} hint={t('When set, notifications are produced only for the patient with this ID.')}>
                                <Input
                                    allowClear
                                    placeholder={t('Empty = all patients')}
                                    value={global.testCustomId}
                                    onChange={(e) => setGlobal(g => ({ ...g, testCustomId: e.target.value }))}
                                />
                            </Field>
                        </Section>
                    </div>
                </div>

                <Section title={t('SMS Balance Alert')}>
                    <div style={{ display: 'flex', gap: 32, flexWrap: 'wrap' }}>
                        <div style={{ flex: '0 0 auto' }}>
                            <Field label={t('SMS balance alert threshold')} hint={t('0 = off. Alerts when remaining SMS drops below this.')}>
                                <InputNumber
                                    min={0}
                                    style={{ width: 200 }}
                                    placeholder="0"
                                    value={global.smsBalanceThreshold}
                                    onChange={(v) => setGlobal(g => ({ ...g, smsBalanceThreshold: v }))}
                                />
                            </Field>
                        </div>
                        <div style={{ flex: 1, minWidth: 260 }}>
                            <Field label={t('Balance alert recipient numbers')} hint={t('Comma-separated phone numbers.')}>
                                <Input
                                    allowClear
                                    placeholder="905341234567, 905361234567"
                                    value={global.smsBalanceAlertRecipients}
                                    onChange={(e) => setGlobal(g => ({ ...g, smsBalanceAlertRecipients: e.target.value }))}
                                />
                            </Field>
                        </div>
                    </div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                        <Button onClick={queryBalance} loading={balanceLoading}>
                            {t('Check balance')}
                        </Button>
                        {balanceResult && (
                            (balanceResult.otpSms != null || balanceResult.sms != null)
                                ? <>
                                    {balanceResult.otpSms != null && (
                                        <Tag color="success" style={{ margin: 0 }}>{t('OTP SMS')}: {balanceResult.otpSms}</Tag>
                                    )}
                                    {balanceResult.sms != null && (
                                        <Tag color="success" style={{ margin: 0 }}>{t('SMS')}: {balanceResult.sms}</Tag>
                                    )}
                                  </>
                                : <Tag color="warning" style={{ margin: 0 }}>{balanceResult.message || t('Could not read balance')}</Tag>
                        )}
                    </div>
                    {balanceResult && (
                        <div style={{ marginTop: 12 }}>
                            <div style={{ fontSize: 12, color: '#8c8c8c', marginBottom: 4 }}>
                                {t('Provider raw response')}
                            </div>
                            <Input.TextArea
                                readOnly
                                autoSize={{ minRows: 3, maxRows: 12 }}
                                value={balanceResult.raw || t('No response received from provider.')}
                                style={{ fontFamily: 'monospace', fontSize: 12 }}
                            />
                        </div>
                    )}
                </Section>
            </div>
                        ),
                    },
                    {
                        key: 'documents',
                        label: t('Documents'),
                        children: (
            <div style={{ maxWidth: 1000 }}>
                <div style={{ fontSize: 12, color: '#8c8c8c', marginBottom: 12 }}>
                    {t('Each upload is a new version of its type; only the active version of each type is referenced. The link is per version. Use {AYDINLATMA_URL} / {TICARI_URL} in the first-contact (KVKK_NOTICE) message.')}
                </div>
                <Space style={{ marginBottom: 12 }} wrap>
                    <Upload accept="application/pdf" beforeUpload={uploadKvkkDoc('AYDINLATMA')} showUploadList={false} maxCount={1}>
                        <Button icon={<UploadOutlined />} loading={kvkkUploading === 'AYDINLATMA'}>{t('Upload Clarification Text (PDF)')}</Button>
                    </Upload>
                    <Upload accept="application/pdf" beforeUpload={uploadKvkkDoc('TICARI')} showUploadList={false} maxCount={1}>
                        <Button icon={<UploadOutlined />} loading={kvkkUploading === 'TICARI'}>{t('Upload Ticari (PDF)')}</Button>
                    </Upload>
                </Space>
                <Table
                    size="small"
                    rowKey="id"
                    pagination={false}
                    dataSource={kvkkDocs}
                    locale={{ emptyText: t('No document uploaded yet.') }}
                    columns={[
                        { title: t('Type'), dataIndex: 'documentType', width: 120, render: (v) => t(DOC_TYPE_LABELS[v] || v) },
                        { title: t('Version'), dataIndex: 'version', width: 70, render: (v) => `v${v}` },
                        { title: t('File'), dataIndex: 'fileName', width: 180, ellipsis: true },
                        {
                            title: t('Page link'), dataIndex: 'sourceUrl',
                            render: (url, row) => (
                                <Input
                                    size="small"
                                    defaultValue={url || ''}
                                    placeholder="https://kurum.com/..."
                                    onBlur={(e) => saveKvkkDocUrl(row.id, e.target.value, url)}
                                    onPressEnter={(e) => e.target.blur()}
                                />
                            ),
                        },
                        { title: t('Date'), dataIndex: 'createdDate', width: 150, render: (d) => d ? new Date(d).toLocaleString('tr-TR') : '—' },
                        {
                            title: t('Active'), dataIndex: 'isActive', width: 70, align: 'center',
                            render: (isActive, row) => (
                                <Radio checked={isActive} onChange={() => activateKvkkDoc(row.id)} />
                            ),
                        },
                        {
                            title: '', key: 'download', width: 100, align: 'right',
                            render: (_, row) => (
                                <Button size="small" icon={<DownloadOutlined />}
                                    onClick={() => ApiService.NotificationSettings.DownloadKvkkDocument(row.id)}>
                                    {t('Download')}
                                </Button>
                            ),
                        },
                    ]}
                />
            </div>
                        ),
                    },
                    {
                        key: 'notifications',
                        label: t('Notification Settings'),
                        children: (
            <Table
                size="small"
                rowKey="eventType"
                columns={columns}
                dataSource={rules}
                pagination={false}
            />
                        ),
                    },
                    {
                        key: 'outbox',
                        label: t('Sent Notifications'),
                        children: (
                            <DataGridProvider>
                                <NotificationOutboxFilter />
                                <NotificationOutboxGrid />
                            </DataGridProvider>
                        ),
                    },
                ]}
            />

            {activeTab !== 'outbox' && (
                <div style={{ textAlign: 'right', marginTop: 16 }}>
                    <Button type="primary" loading={saving} onClick={save}>{t('Save')}</Button>
                </div>
            )}

            <Modal
                title={editingRule ? `${t('Edit Notification')} — ${t(EVENT_LABELS[editingRule.eventType] || editingRule.eventType)}` : t('Edit Notification')}
                open={!!editingRule}
                onCancel={() => setEditingEvent(null)}
                width={680}
                footer={[
                    <Button key="close" type="primary" onClick={() => setEditingEvent(null)}>{t('Close')}</Button>,
                ]}
                destroyOnClose
            >
                {editingRule && (
                    <Tabs
                        items={[
                            {
                                key: 'settings',
                                label: t('Settings'),
                                children: (
                                    <Space direction="vertical" size="middle" style={{ width: '100%' }}>
                                        <SwitchRow
                                            label={t('Enable Notifications')}
                                            checked={editingRule.enabled}
                                            onChange={(v) => updateRule(editingRule.eventType, { enabled: v })}
                                        />
                                        {isAppointmentEvent(editingRule.eventType) && (
                                            <div>
                                                <SwitchRow
                                                    label={t('Requires commercial-message consent')}
                                                    checked={editingRule.requiresKvkk}
                                                    onChange={(v) => updateRule(editingRule.eventType, { requiresKvkk: v })}
                                                />
                                                <div style={{ fontSize: 12, color: '#bfbfbf', marginTop: 4, marginLeft: 44 }}>
                                                    {t('Mark this as a commercial/marketing message. Then it is sent only to patients who gave commercial-message consent; others are skipped.')}
                                                </div>
                                                <SwitchRow
                                                    label={t('Mask patient name')}
                                                    checked={editingRule.maskName}
                                                    onChange={(v) => updateRule(editingRule.eventType, { maskName: v })}
                                                />
                                                <div style={{ fontSize: 12, color: '#bfbfbf', marginTop: 4, marginLeft: 44 }}>
                                                    {t('When on, the patient name/surname is masked in this notification (e.g. "ESEF BERFU" → "E*** B****").')}
                                                </div>
                                            </div>
                                        )}
                                        {isAppointmentEvent(editingRule.eventType) && (editingRule.eventType === 'APPOINTMENT_REMINDER' ? (
                                            <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                                                <div style={{ fontSize: 12, color: '#8c8c8c' }}>
                                                    {t('Reminders are sent at fixed local times. Early appointments are reminded the previous evening.')}
                                                </div>
                                                <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                                                    <span>{t('Morning reminder hour')}</span>
                                                    <InputNumber
                                                        min={0} max={23}
                                                        style={{ width: 90 }}
                                                        placeholder="9"
                                                        value={editingRule.morningHour}
                                                        onChange={(v) => updateRule(editingRule.eventType, { morningHour: v })}
                                                    />
                                                    <span>{t('Evening reminder hour (for early appointments)')}</span>
                                                    <InputNumber
                                                        min={0} max={23}
                                                        style={{ width: 90 }}
                                                        placeholder="21"
                                                        value={editingRule.eveningHour}
                                                        onChange={(v) => updateRule(editingRule.eventType, { eveningHour: v })}
                                                    />
                                                    <span>{t('Early appointment threshold hour')}</span>
                                                    <InputNumber
                                                        min={0} max={23}
                                                        style={{ width: 90 }}
                                                        placeholder="11"
                                                        value={editingRule.earlyThresholdHour}
                                                        onChange={(v) => updateRule(editingRule.eventType, { earlyThresholdHour: v })}
                                                    />
                                                </div>
                                            </div>
                                        ) : (
                                            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                                                <span>{t('Send how many minutes after the event')}</span>
                                                <InputNumber
                                                    min={0}
                                                    style={{ width: 120 }}
                                                    value={editingRule.delayMinutes}
                                                    placeholder="0"
                                                    onChange={(v) => updateRule(editingRule.eventType, { delayMinutes: v })}
                                                />
                                            </div>
                                        ))}
                                        {editingRule.eventType === 'APPOINTMENT_RESCHEDULED' && (
                                            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                                                <span>{t('Minimum time shift to notify (minutes)')}</span>
                                                <InputNumber
                                                    min={0}
                                                    style={{ width: 120 }}
                                                    value={editingRule.thresholdMinutes}
                                                    placeholder="0"
                                                    onChange={(v) => updateRule(editingRule.eventType, { thresholdMinutes: v })}
                                                />
                                            </div>
                                        )}
                                        {!isAppointmentEvent(editingRule.eventType) && (
                                            <div style={{ fontSize: 12, color: '#8c8c8c' }}>
                                                {t('Only the message text is edited here. Enabling/disabling KVKK and login codes is managed in Global Settings. Use the {CODE} placeholder for the verification code.')}
                                            </div>
                                        )}
                                    </Space>
                                ),
                            },
                            ...CHANNELS.map(c => {
                                const channelOn = (editingRule.channels || []).includes(c.value);
                                return {
                                    key: String(c.value),
                                    label: c.label,
                                    children: (
                                        <div>
                                            <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 12 }}>
                                                <Switch
                                                    size="small"
                                                    checked={channelOn}
                                                    onChange={(v) => toggleChannel(editingRule.eventType, c.value, v)}
                                                />
                                                <span>{t('Send via this channel')}</span>
                                            </div>
                                            <NotificationTemplateEditor
                                                title={editingRule.templates[c.value]?.title}
                                                body={editingRule.templates[c.value]?.body}
                                                onTitleChange={(val) => updateTemplate(editingRule.eventType, c.value, { title: val })}
                                                onBodyChange={(val) => updateTemplate(editingRule.eventType, c.value, { body: val })}
                                                placeholders={placeholders}
                                            />
                                        </div>
                                    ),
                                };
                            }),
                        ]}
                    />
                )}
            </Modal>
        </div>
    );
}

function SwitchRow({ label, checked, onChange }) {
    return (
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <Switch checked={checked} onChange={onChange} />
            <span>{label}</span>
        </div>
    );
}

function Section({ title, children }) {
    return (
        <div style={{ marginBottom: 28 }}>
            <div style={{ fontSize: 14, fontWeight: 600, marginBottom: 12, paddingBottom: 8, borderBottom: '1px solid #f0f0f0' }}>
                {title}
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
                {children}
            </div>
        </div>
    );
}

function Field({ label, hint, children }) {
    return (
        <div>
            <div style={{ fontSize: 12, color: '#8c8c8c', marginBottom: 4 }}>{label}</div>
            {children}
            {hint && <div style={{ fontSize: 12, color: '#bfbfbf', marginTop: 4 }}>{hint}</div>}
        </div>
    );
}
